import * as React from "react";
import { useLocation } from "wouter";
import { Zap, Plus, Trash2, Camera, Check, Loader2, ChevronLeft, ChevronRight, RefreshCw, Sparkles, Clock, Pencil, AlertTriangle, BarChart3, TrendingUp, MapPin, Filter, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { Modal } from "@/components/Modal.js";
import { AdSlot } from "@/components/AdSlot.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { useToast } from "@/components/Toast.js";
import { useConfetti } from "@/hooks/useConfetti.js";
import { LoadError } from "@/components/LoadError.js";

interface GuidedFlow {
  id: string; title: string; description: string | null; frequency: string; nextDueDate: string;
  lastCompletedAt: string | null; estimatedMinutes: number | null; missedCount: number; lastFeedback: string | null;
  spaceId: string | null;
  spaceName: string | null;
  tasks: { id: string; label: string; isCompleted: boolean }[];
  trialStatus: "trial" | "kept" | "discarded";
  trialCompletionCount: number;
  trialReviewAfter: number;
  trialReadyForReview: boolean;
}

interface SuggestedFlow {
  title: string; description: string; frequency: string; estimatedMinutes: number; tasks: string[];
}

interface OnboardingStatus {
  hasSpaces: boolean; hasItems: boolean; hasIntent: boolean; hasReachedMilestone: boolean; spaceCount: number; itemCount: number;
}

interface HomeProfile { homeType: string; tidiness: string; }

interface CompletionEntry { id: string; flowId: string; feedback: string | null; completedAt: string; flowTitle: string; }

interface SpaceInfo { id: string; name: string; type: string; }

const FREQUENCIES = ["daily", "weekly", "monthly", "quarterly", "yearly"];

const RESET_STEPS = [
  { title: "Decide what area to tackle", desc: "Pick one surface, shelf, or corner to focus on." },
  { title: "Clearing the space", desc: "Remove everything from the area. Yes, everything." },
  { title: "Sorting items", desc: "Group items: keep, relocate, donate, or discard." },
  { title: "Allocating clutter", desc: "Put each item in its proper home. If it doesn't have one, create one." },
];

// Shared field style (Warm Order input: white fill, clay hairline, 6px radius, amethyst focus ring).
const FIELD = "w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30";

/**
 * RoutinesPage — the canonical home for recurring maintenance routines (guided-flows).
 *
 * This is the "repeat maintenance" layer of Organize, extracted wholesale from the old
 * WorkflowsPage (which was doing double duty as both the hub and the routines tool). The
 * lean hub now lives in OrganizeHubPage; routine creation, trials, missed-flow nudges,
 * 30-day trends, Quick Reset, and completion analytics all live here.
 */
export default function RoutinesPage() {
  const [, navigate] = useLocation();
  const { aiEnabled } = useFeatureFlags();
  const toast = useToast();
  const confetti = useConfetti();
  const [flows, setFlows] = React.useState<GuidedFlow[]>([]);
  const [spaces, setSpaces] = React.useState<SpaceInfo[]>([]);
  const [onboarding, setOnboarding] = React.useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [filterSpaceId, setFilterSpaceId] = React.useState<string | null>(null);

  // Create/Edit flow
  const [showCreate, setShowCreate] = React.useState(false);
  const [editingFlowId, setEditingFlowId] = React.useState<string | null>(null);
  const [newTitle, setNewTitle] = React.useState("");
  const [newDesc, setNewDesc] = React.useState("");
  const [newFreq, setNewFreq] = React.useState("weekly");
  const [newEstimate, setNewEstimate] = React.useState("");
  const [newTasks, setNewTasks] = React.useState<string[]>([""]);
  const [newDueDate, setNewDueDate] = React.useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().split("T")[0];
  });
  const [saving, setSaving] = React.useState(false);

  // Add Flow (unified: suggestions + manual)
  const [showAddFlow, setShowAddFlow] = React.useState(false);
  const [homeProfile, setHomeProfile] = React.useState<HomeProfile | null | undefined>(undefined);
  const [showProfileQuestions, setShowProfileQuestions] = React.useState(false);
  const [profileHomeType, setProfileHomeType] = React.useState("house");
  const [profileTidiness, setProfileTidiness] = React.useState("some_clutter");
  const [suggestions, setSuggestions] = React.useState<SuggestedFlow[]>([]);
  const [suggestLoading, setSuggestLoading] = React.useState(false);

  // Feedback
  const [feedbackFlowId, setFeedbackFlowId] = React.useState<string | null>(null);
  const [streakMilestone, setStreakMilestone] = React.useState<number | null>(null);

  // Completion history
  const [completions, setCompletions] = React.useState<CompletionEntry[]>([]);
  const [showTrends, setShowTrends] = React.useState(false);

  // Quick Reset
  const [showReset, setShowReset] = React.useState(false);
  const [resetMode, setResetMode] = React.useState<"choose" | "generic" | "photo">("choose");
  const [resetStep, setResetStep] = React.useState(0);
  const [resetAnalysis, setResetAnalysis] = React.useState<{ items: string[]; locations: string[] } | null>(null);
  const [resetChecks, setResetChecks] = React.useState<Set<number>>(new Set());
  const [resetLoading, setResetLoading] = React.useState(false);
  const [resetDone, setResetDone] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoadError(false);
    try {
      const [f, o, p, c, s] = await Promise.all([
        apiFetch<GuidedFlow[]>("/api/guided-flows"),
        apiFetch<OnboardingStatus>("/api/onboarding/status"),
        apiFetch<HomeProfile | null>("/api/home-profile"),
        apiFetch<CompletionEntry[]>("/api/guided-flows/completions?days=30"),
        apiFetch<SpaceInfo[]>("/api/spaces"),
      ]);
      setFlows(f); setOnboarding(o); setHomeProfile(p); setCompletions(c); setSpaces(s);
    } catch {
      // Don't fall through to an empty "no routines yet" — say it failed and offer retry.
      setLoadError(true);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => { fetchData(); }, [fetchData]);

  // --- Handlers ---

  const handleDelete = async (id: string) => {
    await apiFetch(`/api/guided-flows/${id}`, { method: "DELETE" });
    setFlows((prev) => prev.filter((f) => f.id !== id));
  };

  const handleTrialDecision = async (id: string, decision: "kept" | "discarded") => {
    try {
      const updated = await apiFetch<GuidedFlow>(`/api/guided-flows/${id}/trial-decision`, { method: "POST", body: JSON.stringify({ decision }) });
      if (decision === "kept") {
        toast.show("Routine kept! 🎉 It's now a permanent part of your workflow.");
        confetti.burst();
      } else {
        toast.show("Trial discarded. No worries — try something else!");
      }
      setFlows((prev) => prev.map((f) => f.id === id ? { ...f, ...updated } : f));
    } catch (e: any) {
      toast.show(e.message ?? "Failed to save decision", "error");
    }
  };

  const openCreateModal = (suggestion?: SuggestedFlow) => {
    setEditingFlowId(null);
    setNewTitle(suggestion?.title ?? "");
    setNewDesc(suggestion?.description ?? "");
    setNewFreq(suggestion?.frequency ?? "weekly");
    setNewEstimate(suggestion?.estimatedMinutes ? String(suggestion.estimatedMinutes) : "");
    setNewTasks(suggestion?.tasks?.length ? suggestion.tasks : [""]);
    const d = new Date(); d.setDate(d.getDate() + 7);
    setNewDueDate(d.toISOString().split("T")[0]);
    setShowAddFlow(false);
    setShowCreate(true);
  };

  const openEditModal = (flow: GuidedFlow) => {
    setEditingFlowId(flow.id);
    setNewTitle(flow.title);
    setNewDesc(flow.description ?? "");
    setNewFreq(flow.frequency);
    setNewEstimate(flow.estimatedMinutes ? String(flow.estimatedMinutes) : "");
    setNewTasks(flow.tasks.length ? flow.tasks.map((t) => t.label) : [""]);
    setNewDueDate(new Date(flow.nextDueDate).toISOString().split("T")[0]);
    setShowCreate(true);
  };

  const handleSaveFlow = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    const tasks = newTasks.filter((t) => t.trim()).map((label, i) => ({ id: String(i + 1), label, isCompleted: false }));
    const body = {
      title: newTitle, description: newDesc || null, frequency: newFreq,
      nextDueDate: new Date(newDueDate + "T00:00:00").toISOString(),
      estimatedMinutes: newEstimate ? Number(newEstimate) : null, tasks,
    };
    try {
      if (editingFlowId) {
        await apiFetch(`/api/guided-flows/${editingFlowId}`, { method: "PUT", body: JSON.stringify(body) });
      } else {
        await apiFetch("/api/guided-flows", { method: "POST", body: JSON.stringify(body) });
      }
      setShowCreate(false); fetchData();
    } catch {
      toast.show("Couldn't save that routine. Please try again.", "error");
    }
    setSaving(false);
  };

  const handleAddFlow = async () => {
    if (!aiEnabled) {
      // No AI — go straight to manual form
      openCreateModal();
      return;
    }
    // AI enabled — open the Add Flow modal and fetch suggestions
    setShowAddFlow(true);
    if (!homeProfile) {
      // Need profile first
      setShowProfileQuestions(true);
      setShowAddFlow(false);
      return;
    }
    setSuggestLoading(true);
    try {
      const { suggestions: s } = await apiFetch<{ suggestions: SuggestedFlow[] }>("/api/ai/suggest-flows", { method: "POST" });
      setSuggestions(s);
    } catch (e: any) {
      toast.show(e.message?.includes("limit") ? "AI action limit reached" : "Failed to get suggestions", "error");
    }
    setSuggestLoading(false);
  };

  const handleSaveProfile = async () => {
    try {
      const profile = await apiFetch<HomeProfile>("/api/home-profile", { method: "POST", body: JSON.stringify({ homeType: profileHomeType, tidiness: profileTidiness }) });
      setHomeProfile(profile);
      setShowProfileQuestions(false);
      // Now open the Add Flow modal and fetch suggestions
      setShowAddFlow(true);
      setSuggestLoading(true);
      const { suggestions: s } = await apiFetch<{ suggestions: SuggestedFlow[] }>("/api/ai/suggest-flows", { method: "POST" });
      setSuggestions(s);
      setSuggestLoading(false);
    } catch { toast.show("Failed to save profile", "error"); setSuggestLoading(false); }
  };

  const handleAcceptSuggestion = async (s: SuggestedFlow) => {
    const tasks = s.tasks.map((label, i) => ({ id: String(i + 1), label, isCompleted: false }));
    const d = new Date(); d.setDate(d.getDate() + (s.frequency === "daily" ? 1 : 7));
    try {
      await apiFetch("/api/guided-flows", { method: "POST", body: JSON.stringify({
        title: s.title, description: s.description, frequency: s.frequency,
        nextDueDate: d.toISOString(), estimatedMinutes: s.estimatedMinutes, tasks,
      }) });
      setSuggestions((prev) => prev.filter((x) => x.title !== s.title));
      fetchData();
      toast.show(`Added "${s.title}"`);
    } catch {
      toast.show("Couldn't add that routine. Please try again.", "error");
    }
  };

  const handleSkipSuggestion = async (s: SuggestedFlow) => {
    setSuggestions((prev) => prev.filter((x) => x.title !== s.title));
    await apiFetch("/api/skipped-suggestions", { method: "POST", body: JSON.stringify({ title: s.title }) }).catch(() => {});
  };

  const handleEditSuggestion = (s: SuggestedFlow) => {
    openCreateModal(s);
  };

  const handleTaskToggle = async (flow: GuidedFlow, taskId: string) => {
    const updatedTasks = flow.tasks.map((t) => t.id === taskId ? { ...t, isCompleted: !t.isCompleted } : t);
    setFlows((prev) => prev.map((f) => f.id === flow.id ? { ...f, tasks: updatedTasks } : f));
    const result = await apiFetch<GuidedFlow>(`/api/guided-flows/${flow.id}`, { method: "PUT", body: JSON.stringify({ tasks: updatedTasks }) });
    setFlows((prev) => prev.map((f) => f.id === flow.id ? result : f));
    if (result.lastCompletedAt && result.lastCompletedAt !== flow.lastCompletedAt) {
      confetti.burst();
      // Check streak milestone (include today's new completion)
      const days = new Set(completions.map((c) => new Date(c.completedAt).toDateString()));
      days.add(new Date().toDateString());
      let streak = 0;
      const d = new Date();
      while (days.has(d.toDateString())) { streak++; d.setDate(d.getDate() - 1); }
      if ([7, 14, 30].includes(streak)) {
        setStreakMilestone(streak);
        confetti.streak();
      }
      setFeedbackFlowId(flow.id);
      fetchData(); // Refresh completions
    }
  };

  const handleFeedback = async (rating: string) => {
    if (!feedbackFlowId) return;
    await apiFetch(`/api/guided-flows/${feedbackFlowId}/feedback`, { method: "POST", body: JSON.stringify({ rating }) }).catch(() => {});
    const flow = flows.find((f) => f.id === feedbackFlowId);
    const milestone = streakMilestone;
    if (milestone) {
      toast.show(`🔥 ${milestone}-day streak! The habit is forming.`);
      setStreakMilestone(null);
    } else {
      toast.show(`Flow complete! 🎉 Next up on ${flow ? new Date(flow.nextDueDate).toLocaleDateString() : "schedule"}`);
    }
    setFeedbackFlowId(null);
  };

  const handleAdjust = async (flow: GuidedFlow, action: "snooze" | "monthly" | "remove") => {
    if (action === "remove") { handleDelete(flow.id); return; }
    if (action === "snooze") {
      const d = new Date(); d.setDate(d.getDate() + 7);
      await apiFetch(`/api/guided-flows/${flow.id}`, { method: "PUT", body: JSON.stringify({ nextDueDate: d.toISOString(), missedCount: 0 }) });
    } else {
      await apiFetch(`/api/guided-flows/${flow.id}`, { method: "PUT", body: JSON.stringify({ frequency: "monthly", missedCount: 0 }) });
    }
    fetchData();
  };

  const handleResetPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(",")[1];
      setResetLoading(true);
      try {
        const result = await apiFetch<{ items: string[]; locations: string[] }>("/api/ai/analyze-reset", { method: "POST", body: JSON.stringify({ imageBase64: base64, mimeType: file.type }) });
        setResetAnalysis(result); setResetMode("photo");
      } catch (err: any) {
        toast.show(err?.message?.includes("limit") ? "AI action limit reached" : "Couldn't analyze that photo. Please try again.", "error");
      }
      setResetLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const missedFlows = (filterSpaceId
    ? flows.filter((f) => f.spaceId === filterSpaceId)
    : flows
  ).filter((f) => f.missedCount >= 3);

  // Space name lookup for flow cards
  const spaceNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const s of spaces) map.set(s.id, s.name);
    return map;
  }, [spaces]);

  // Filter flows by space if filter is active
  const filteredFlows = filterSpaceId
    ? flows.filter((f) => f.spaceId === filterSpaceId)
    : flows;

  // Spaces that have at least one flow linked
  const spacesWithFlows = React.useMemo(() => {
    const ids = new Set(flows.map((f) => f.spaceId).filter(Boolean));
    return spaces.filter((s) => ids.has(s.id));
  }, [flows, spaces]);

  if (loading) return <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-32 bg-clay-muted rounded-lg animate-pulse" />)}</div>;

  if (loadError) return <LoadError onRetry={() => { setLoading(true); fetchData(); }} />;

  // Space-aware empty state: no gate, but guide user if they have no flows
  const showEmptyState = flows.length === 0 && !loading;

  return (
    <div className="space-y-8">
      {/* Back to hub */}
      <button onClick={() => navigate("/organize")} className="flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors">
        <ArrowLeft className="w-4 h-4" />Organize
      </button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink flex items-center gap-2 font-headline tracking-[-0.01em]"><Zap className="w-6 h-6 text-primary" />Routines</h1>
        <button onClick={handleAddFlow} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-1 hover:bg-amethyst-ink transition-colors"><Plus className="w-4 h-4" />Add Routine</button>
      </div>

      {/* Empty state — space-aware guidance instead of hard gate */}
      {showEmptyState && (
        <div className="bg-sage-wash rounded-lg border border-clay-border p-8 text-center">
          <div className="text-4xl mb-3">✨</div>
          {onboarding && !onboarding.hasSpaces ? (
            <>
              <h2 className="text-[1.0625rem] font-semibold text-ink mb-2">Set up your spaces first</h2>
              <p className="text-sm text-body mb-6 max-w-sm mx-auto">
                Add your rooms so we can suggest routines tailored to each space.
              </p>
              <button onClick={() => navigate("/spaces")} className="inline-flex px-6 py-3 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Add Spaces</button>
            </>
          ) : (
            <>
              <h2 className="text-[1.0625rem] font-semibold text-ink mb-2">No routines yet</h2>
              <p className="text-sm text-body mb-6 max-w-sm mx-auto">
                Create your first routine to keep your home organized. Set an intent on a room for personalized suggestions.
              </p>
              <div className="flex justify-center gap-3">
                <button onClick={handleAddFlow} className="px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Create a routine</button>
                <button onClick={() => navigate("/spaces")} className="px-5 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-clay-muted transition-colors">Set up a room</button>
              </div>
            </>
          )}
        </div>
      )}

      {!showEmptyState && (
        <>

      {/* Space filter — only show when there are flows linked to spaces */}
      {spacesWithFlows.length > 0 && (
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-faint" />
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setFilterSpaceId(null)}
              className={cn("px-3 py-1 rounded-pill text-[0.8125rem] font-medium transition-colors", !filterSpaceId ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-amethyst-soft")}
            >
              All
            </button>
            {spacesWithFlows.map((s) => (
              <button
                key={s.id}
                onClick={() => setFilterSpaceId(filterSpaceId === s.id ? null : s.id)}
                className={cn("px-3 py-1 rounded-pill text-[0.8125rem] font-medium transition-colors", filterSpaceId === s.id ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-amethyst-soft")}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Adjustment prompts for missed flows */}
      {missedFlows.map((flow) => (
        <div key={`missed-${flow.id}`} className="flex items-start gap-3 p-4 rounded-lg bg-warning-wash border border-clay-border">
          <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-warning-text">"{flow.title}" has been missed {flow.missedCount} times</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => handleAdjust(flow, "snooze")} className="px-3 py-1 rounded-md text-[0.8125rem] font-medium bg-white border border-clay-border text-warning-text hover:bg-paper transition-colors">Snooze 1 week</button>
              <button onClick={() => handleAdjust(flow, "monthly")} className="px-3 py-1 rounded-md text-[0.8125rem] font-medium bg-white border border-clay-border text-warning-text hover:bg-paper transition-colors">Make monthly</button>
              <button onClick={() => handleAdjust(flow, "remove")} className="px-3 py-1 rounded-md text-[0.8125rem] font-medium bg-white border border-clay-border text-error-text hover:bg-error-wash transition-colors">Remove</button>
            </div>
          </div>
        </div>
      ))}

      {/* Completion Trends */}
      {completions.length > 0 && (
        <div className="bg-white rounded-lg border border-clay-border p-5 space-y-3">
          <button onClick={() => setShowTrends(!showTrends)} className="flex items-center justify-between w-full">
            <h2 className="text-[1.0625rem] font-semibold text-ink flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />30-Day Trends</h2>
            <span className="text-[0.8125rem] text-faint">{showTrends ? "Hide" : "Show"}</span>
          </button>
          {/* Summary stats always visible */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-md bg-amethyst-soft">
              <div className="text-xl font-semibold text-amethyst-ink tabular-nums">{completions.length}</div>
              <div className="text-[0.8125rem] text-body">Completions</div>
            </div>
            <div className="text-center p-3 rounded-md bg-sage-wash">
              <div className="text-xl font-semibold text-sage-deep tabular-nums">{new Set(completions.map((c) => c.flowId)).size}</div>
              <div className="text-[0.8125rem] text-body">Active Flows</div>
            </div>
            <div className="text-center p-3 rounded-md bg-success-wash">
              <div className="text-xl font-semibold text-success-text tabular-nums">
                {(() => {
                  let streak = 0;
                  const days = new Set(completions.map((c) => new Date(c.completedAt).toDateString()));
                  const d = new Date();
                  while (days.has(d.toDateString())) { streak++; d.setDate(d.getDate() - 1); }
                  return streak;
                })()}
              </div>
              <div className="text-[0.8125rem] text-body">Day Streak</div>
            </div>
          </div>
          {showTrends && (
            <div className="space-y-3 pt-2">
              {/* Weekly bar chart */}
              <div>
                <h3 className="text-[0.8125rem] font-medium text-body mb-2 flex items-center gap-1"><BarChart3 className="w-3 h-3" />Weekly</h3>
                <div className="flex items-end gap-1 h-16">
                  {(() => {
                    const weeks: number[] = [0, 0, 0, 0];
                    const now = Date.now();
                    completions.forEach((c) => {
                      const age = Math.floor((now - new Date(c.completedAt).getTime()) / (7 * 86400000));
                      if (age < 4) weeks[3 - age]++;
                    });
                    const max = Math.max(...weeks, 1);
                    return weeks.map((count, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full rounded-t-sm bg-primary" style={{ height: `${(count / max) * 100}%`, minHeight: count ? 4 : 0 }} />
                        <span className="text-[0.8125rem] text-faint tabular-nums">{i === 3 ? "This" : `${3 - i}w`}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              {/* Per-flow breakdown */}
              <div>
                <h3 className="text-[0.8125rem] font-medium text-body mb-2">By Flow</h3>
                <div className="space-y-1">
                  {Object.entries(completions.reduce<Record<string, number>>((acc, c) => { acc[c.flowTitle] = (acc[c.flowTitle] || 0) + 1; return acc; }, {}))
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([title, count]) => (
                      <div key={title} className="flex justify-between text-[0.8125rem]">
                        <span className="text-body truncate mr-2">{title}</span>
                        <span className="font-medium text-primary tabular-nums">{count}×</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Flow cards grouped by time */}
      {filteredFlows.length > 0 && (() => {
        const now = new Date();
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        const weekEnd = new Date(todayEnd); weekEnd.setDate(weekEnd.getDate() + (7 - weekEnd.getDay()));
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        const groups = [
          { label: "Today", items: filteredFlows.filter((f) => new Date(f.nextDueDate) <= todayEnd) },
          { label: "This Week", items: filteredFlows.filter((f) => { const d = new Date(f.nextDueDate); return d > todayEnd && d <= weekEnd; }) },
          { label: "This Month", items: filteredFlows.filter((f) => { const d = new Date(f.nextDueDate); return d > weekEnd && d <= monthEnd; }) },
          { label: "Later", items: filteredFlows.filter((f) => new Date(f.nextDueDate) > monthEnd) },
        ].filter((g) => g.items.length > 0);

        return groups.map((group) => (
          <div key={group.label} className="space-y-4">
            <h2 className="text-[1.0625rem] font-semibold text-ink">{group.label}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {group.items.filter((f) => f.trialStatus !== "discarded").map((flow) => (
                <div key={flow.id} className={cn("bg-white rounded-lg border p-6 space-y-4 group transition-colors", flow.trialReadyForReview ? "border-success ring-1 ring-success/30" : "border-clay-border hover:border-amethyst/40")}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-[1.0625rem] font-semibold text-ink">{flow.title}</h3>
                      {flow.trialStatus === "trial" && (
                        <span className="px-2 py-0.5 rounded-pill text-[0.8125rem] font-medium bg-warning-wash text-warning-text tabular-nums">
                          Trial · {flow.trialCompletionCount}/{flow.trialReviewAfter}
                        </span>
                      )}
                      {flow.trialStatus === "kept" && (
                        <span className="px-2 py-0.5 rounded-pill text-[0.8125rem] font-medium bg-success-wash text-success-text">
                          ✓ Kept
                        </span>
                      )}
                      {flow.spaceId && spaceNameMap.has(flow.spaceId) && (
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/spaces/${flow.spaceId}`); }} className="px-2 py-0.5 rounded-pill text-[0.8125rem] font-medium bg-amethyst-soft text-amethyst-ink flex items-center gap-0.5 hover:bg-clay-muted transition-colors">
                          <MapPin className="w-2.5 h-2.5" />{spaceNameMap.get(flow.spaceId)}
                        </button>
                      )}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEditModal(flow)} className="text-faint hover:text-primary transition-colors" aria-label={`Edit ${flow.title}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(flow.id)} className="text-faint hover:text-error transition-colors" aria-label={`Delete ${flow.title}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {/* Trial review prompt */}
                  {flow.trialReadyForReview && (
                    <div className="p-3 rounded-md bg-success-wash border border-clay-border space-y-2">
                      <p className="text-[0.8125rem] font-medium text-success-text">🎯 Trial review: You've completed this {flow.trialCompletionCount} times. Is it working?</p>
                      <div className="flex gap-2">
                        <button onClick={() => handleTrialDecision(flow.id, "kept")} className="px-3 py-1.5 rounded-md bg-success text-white text-[0.8125rem] font-medium hover:brightness-95 transition-all">Keep it ✓</button>
                        <button onClick={() => handleTrialDecision(flow.id, "discarded")} className="px-3 py-1.5 rounded-md bg-white border border-clay-border text-body text-[0.8125rem] font-medium hover:bg-paper transition-colors">Discard</button>
                      </div>
                    </div>
                  )}

                  {flow.description && <p className="text-sm text-body">{flow.description}</p>}
                  {flow.tasks?.length > 0 && (
                    <div className="space-y-1">
                      {flow.tasks.map((task) => (
                        <button key={task.id} className="flex items-center gap-2 text-sm w-full text-left" onClick={() => handleTaskToggle(flow, task.id)}>
                          <div className={cn("w-4 h-4 rounded-sm border flex items-center justify-center", task.isCompleted ? "bg-primary border-primary" : "border-clay-border")}>
                            {task.isCompleted && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className={task.isCompleted ? "line-through text-faint" : "text-body"}>{task.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-between text-[0.8125rem] text-faint">
                    <div className="flex items-center gap-2">
                      <span className="capitalize">{flow.frequency}</span>
                      {flow.estimatedMinutes && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />~{flow.estimatedMinutes}m</span>}
                      {completions.filter((c) => c.flowId === flow.id).length > 0 && (
                        <span className="flex items-center gap-0.5 tabular-nums"><Check className="w-3 h-3" />{completions.filter((c) => c.flowId === flow.id).length}×</span>
                      )}
                    </div>
                    {new Date(flow.nextDueDate) < new Date() ? (
                      <span className="text-warning-text bg-warning-wash px-2 py-0.5 rounded-pill font-medium">Past due · {new Date(flow.nextDueDate).toLocaleDateString()}</span>
                    ) : (
                      <span>Due: {new Date(flow.nextDueDate).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ));
      })()}

      <AdSlot slot="workflows-bottom" />
        </>
      )}

      {/* Add Flow Modal — AI suggestions with manual fallback */}
      <Modal open={showAddFlow} onClose={() => setShowAddFlow(false)} title="Add Routine" wide>
        <div className="space-y-4">
          {suggestLoading ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <div className="w-12 h-12 rounded-md bg-sage-wash flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-sage-deep animate-spin" />
              </div>
              <p className="text-sm font-medium text-sage-deep">Finding routines that fit your home...</p>
              <p className="text-[0.8125rem] text-faint">This usually takes a few seconds</p>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-6">
              <Sparkles className="w-8 h-8 text-sage-deep mx-auto mb-2" />
              <p className="text-sm text-body">No suggestions right now — create one manually below.</p>
            </div>
          ) : (
            <>
              <p className="text-[0.8125rem] text-faint">Suggested routines based on your home. Start a trial or create your own.</p>
              {suggestions.map((s, i) => (
                <div key={i} className="p-4 rounded-lg border border-clay-border space-y-3">
                  <div>
                    <h3 className="text-[1.0625rem] font-semibold text-ink flex items-center gap-2"><Sparkles className="w-4 h-4 text-sage-deep" />{s.title}</h3>
                    <p className="text-[0.8125rem] text-faint mt-0.5">{s.description}</p>
                  </div>
                  <div className="flex items-center gap-3 text-[0.8125rem] text-faint">
                    <span className="capitalize">{s.frequency}</span>
                    <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />~{s.estimatedMinutes}m</span>
                  </div>
                  <div className="space-y-1">
                    {s.tasks.map((task, j) => (
                      <div key={j} className="flex items-center gap-2 text-sm text-body">
                        <div className="w-4 h-4 rounded-sm border border-clay-border" />
                        <span>{task}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleAcceptSuggestion(s)} className="px-4 py-2 rounded-md bg-sage-deep text-white text-[0.8125rem] font-medium hover:bg-sage-deep-hover transition-colors">Start trial</button>
                    <button onClick={() => handleEditSuggestion(s)} className="px-4 py-2 rounded-md bg-amethyst-soft text-amethyst-ink text-[0.8125rem] font-medium hover:bg-clay-muted transition-colors">Edit first</button>
                    <button onClick={() => handleSkipSuggestion(s)} className="px-4 py-2 rounded-md bg-clay-muted text-body text-[0.8125rem] font-medium hover:bg-amethyst-soft transition-colors">Skip</button>
                  </div>
                </div>
              ))}
            </>
          )}
          {/* Manual creation link */}
          <div className="pt-2 border-t border-clay-border">
            <button onClick={() => openCreateModal()} className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-body hover:text-primary transition-colors">
              <Pencil className="w-4 h-4" />Create manually
            </button>
          </div>
        </div>
      </Modal>

      {/* Create/Edit Flow Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={editingFlowId ? "Edit Routine" : "New Routine"}>
        <div className="space-y-3">
          <input placeholder="Title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className={FIELD} />
          <textarea placeholder="Description (optional)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={2} className={FIELD + " resize-none"} />
          <div>
            <label className="text-sm font-medium text-body mb-1 block">Frequency</label>
            <div className="flex gap-1 flex-wrap">
              {FREQUENCIES.map((f) => (
                <button key={f} onClick={() => setNewFreq(f)} className={cn("px-3 py-1 rounded-pill text-[0.8125rem] font-medium capitalize transition-colors", newFreq === f ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-amethyst-soft")}>{f}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-sm font-medium text-body mb-1 block">Due date</label>
              <input type="date" value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)} className={FIELD} />
            </div>
            <div className="w-24">
              <label className="text-sm font-medium text-body mb-1 block">~Minutes</label>
              <input type="number" min={1} max={480} placeholder="15" value={newEstimate} onChange={(e) => setNewEstimate(e.target.value)} className={FIELD + " tabular-nums"} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-body mb-1 block">Tasks</label>
            {newTasks.map((task, i) => (
              <div key={i} className="flex gap-2 mb-1 items-center">
                <input value={task} onChange={(e) => { const t = [...newTasks]; t[i] = e.target.value; setNewTasks(t); }} placeholder={`Task ${i + 1}`} className={"flex-1 " + FIELD} />
                {newTasks.length > 1 && <button onClick={() => setNewTasks(newTasks.filter((_, j) => j !== i))} className="text-error hover:text-error-text text-sm px-1" aria-label={`Remove task ${i + 1}`}>✕</button>}
              </div>
            ))}
            <button onClick={() => setNewTasks([...newTasks, ""])} className="text-[0.8125rem] text-primary hover:text-amethyst-ink mt-1">+ Add task</button>
          </div>
          <button onClick={handleSaveFlow} disabled={saving || !newTitle.trim()} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : editingFlowId ? "Save Changes" : "Start Trial"}
          </button>
        </div>
      </Modal>

      {/* Home Profile Questions Modal */}
      <Modal open={showProfileQuestions} onClose={() => setShowProfileQuestions(false)} title="Quick Questions">
        <div className="space-y-4">
          <p className="text-sm text-body">Help us personalize your flow suggestions.</p>
          <div>
            <label className="text-sm font-medium text-body mb-2 block">What kind of home do you live in?</label>
            <div className="grid grid-cols-2 gap-2">
              {[["apartment", "🏢 Apartment"], ["house", "🏠 House"], ["condo", "🏙️ Condo"], ["other", "🏘️ Other"]].map(([val, label]) => (
                <button key={val} onClick={() => setProfileHomeType(val)} className={cn("px-4 py-3 rounded-md text-sm font-medium border transition-colors", profileHomeType === val ? "border-primary bg-amethyst-soft text-amethyst-ink" : "border-clay-border text-body hover:border-amethyst/40")}>{label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-body mb-2 block">How would you describe your current tidiness?</label>
            <div className="space-y-2">
              {[["organized", "✨ Pretty organized — just need maintenance"], ["some_clutter", "📦 Some clutter — want to get on top of it"], ["overwhelmed", "🌊 Overwhelmed — need a starting point"]].map(([val, label]) => (
                <button key={val} onClick={() => setProfileTidiness(val)} className={cn("w-full px-4 py-3 rounded-md text-sm text-left font-medium border transition-colors", profileTidiness === val ? "border-primary bg-amethyst-soft text-amethyst-ink" : "border-clay-border text-body hover:border-amethyst/40")}>{label}</button>
              ))}
            </div>
          </div>
          <button onClick={handleSaveProfile} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Get Suggestions</button>
        </div>
      </Modal>

      {/* Post-completion Feedback Modal */}
      <Modal open={!!feedbackFlowId} onClose={() => { setFeedbackFlowId(null); setStreakMilestone(null); toast.show("Flow complete! 🎉"); }} title="How did that feel?">
        {streakMilestone && (
          <div className="text-center py-3 mb-2 rounded-lg bg-warning-wash border border-clay-border">
            <div className="text-2xl">🔥</div>
            <div className="text-sm font-semibold text-warning-text tabular-nums">{streakMilestone}-day streak!</div>
            <div className="text-[0.8125rem] text-warning-text">{streakMilestone >= 30 ? "Incredible discipline." : streakMilestone >= 14 ? "This is becoming second nature." : "The habit is forming."}</div>
          </div>
        )}
        <div className="flex gap-3 py-4">
          {[["😫", "Too much", "too_much"], ["😐", "Fine", "fine"], ["😊", "Easy", "easy"]].map(([emoji, label, value]) => (
            <button key={value} onClick={() => handleFeedback(value)} className="flex-1 flex flex-col items-center gap-2 p-4 rounded-md border border-clay-border hover:border-amethyst/40 transition-colors">
              <span className="text-2xl">{emoji}</span>
              <span className="text-[0.8125rem] font-medium text-body">{label}</span>
            </button>
          ))}
        </div>
      </Modal>

      {/* Quick Reset Modal */}
      <Modal open={showReset} onClose={() => setShowReset(false)} title="Quick Reset">
        {resetDone ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-pill bg-success-wash flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-success" /></div>
            <h3 className="text-[1.0625rem] font-semibold text-ink mb-1">Space reset complete!</h3>
            <p className="text-sm text-body">Great job decluttering.</p>
            <button onClick={() => setShowReset(false)} className="mt-4 px-6 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Done</button>
          </div>
        ) : resetMode === "choose" ? (
          <div className="space-y-3">
            <button onClick={() => setResetMode("generic")} className="w-full flex items-center gap-3 p-4 rounded-lg border border-clay-border hover:border-amethyst/40 text-left transition-colors">
              <RefreshCw className="w-6 h-6 text-primary" />
              <div><div className="font-medium text-sm text-ink">Guided Reset</div><div className="text-[0.8125rem] text-faint">Step-by-step declutter guide</div></div>
            </button>
            {aiEnabled && (
              <label className="w-full flex items-center gap-3 p-4 rounded-lg border border-clay-border hover:border-sage text-left cursor-pointer transition-colors">
                <Camera className="w-6 h-6 text-sage-deep" />
                <div><div className="font-medium text-sm text-ink">Photo Analysis</div><div className="text-[0.8125rem] text-faint">AI identifies out-of-place items</div></div>
                <input type="file" accept="image/*" capture="environment" onChange={handleResetPhoto} className="hidden" />
              </label>
            )}
            {resetLoading && <div className="flex items-center gap-2 text-sm text-body"><Loader2 className="w-4 h-4 animate-spin" />Analyzing photo...</div>}
          </div>
        ) : resetMode === "generic" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {RESET_STEPS.map((_, i) => <div key={i} className={cn("h-1.5 flex-1 rounded-pill", i <= resetStep ? "bg-primary" : "bg-clay-muted")} />)}
            </div>
            <div>
              <h3 className="text-[1.0625rem] font-semibold text-ink">{RESET_STEPS[resetStep].title}</h3>
              <p className="text-sm text-body mt-1">{RESET_STEPS[resetStep].desc}</p>
            </div>
            <div className="flex gap-2">
              {resetStep > 0 && <button onClick={() => setResetStep(resetStep - 1)} className="flex-1 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium flex items-center justify-center gap-1 hover:bg-clay-muted transition-colors"><ChevronLeft className="w-4 h-4" />Back</button>}
              {resetStep < RESET_STEPS.length - 1 ? (
                <button onClick={() => setResetStep(resetStep + 1)} className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium flex items-center justify-center gap-1 hover:bg-amethyst-ink transition-colors">Next<ChevronRight className="w-4 h-4" /></button>
              ) : (
                <button onClick={() => setResetDone(true)} className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Finish Reset</button>
              )}
            </div>
          </div>
        ) : resetMode === "photo" && resetAnalysis ? (
          <div className="space-y-4">
            <p className="text-sm font-medium text-ink">Items that may be out of place:</p>
            <div className="space-y-2">
              {resetAnalysis.items.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-md bg-paper">
                  <button onClick={() => { const next = new Set(resetChecks); next.has(i) ? next.delete(i) : next.add(i); setResetChecks(next); }} aria-label={resetChecks.has(i) ? `Uncheck ${item}` : `Check ${item}`}>
                    <div className={cn("w-5 h-5 rounded-sm border-2 flex items-center justify-center", resetChecks.has(i) ? "bg-primary border-primary" : "border-clay-border")}>
                      {resetChecks.has(i) && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </button>
                  <div className="flex-1 text-sm">
                    <span className="font-medium text-ink">{item}</span>
                    {resetAnalysis.locations[i] && <span className="text-faint ml-2">→ {resetAnalysis.locations[i]}</span>}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setResetDone(true)} disabled={resetChecks.size < resetAnalysis.items.length} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
              {resetChecks.size < resetAnalysis.items.length ? `${resetChecks.size}/${resetAnalysis.items.length} done` : "Finish Reset"}
            </button>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
